import { Button, Group, Paper, Stack, Text, Textarea, Title } from '@mantine/core'
import { useQuery } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useState } from 'react'

import useCheckMobileScreen from '@/hooks/useCheckMobileScreen'
import { useGlobalState } from '@/hooks/useGlobalState'
import { useSetupProgressTracker } from '@/hooks/useSetupProgressTracker'
import type { GetCurrentSponsorPlaceholdersResponse } from '@/services/api/api.types'

interface DraftWelcomeMessagePanelProps {
  showTutorialText?: boolean
  registerUnsavedChangesChecker: (tabKey: 'draftWelcomeMessage', checker: () => boolean, onDiscard: () => void) => void
}

export function DraftWelcomeMessagePanel({
  showTutorialText = false,
  registerUnsavedChangesChecker,
}: DraftWelcomeMessagePanelProps) {
  const { api } = useGlobalState()
  const isMobile = useCheckMobileScreen()
  const [isPending, setIsPending] = useState(false)

  // Get setup progress tracker for marking welcome message step complete
  const { markStepComplete } = useSetupProgressTracker()

  const placeholderTerminologyQuery = useQuery({
    queryKey: ['terminology', 'placeholder'],
    queryFn: () => api.getPlaceholderTerminology(),
    refetchOnMount: 'always',
    staleTime: 0,
  })

  const [originalTerms, setOriginalTerms] = useState<GetCurrentSponsorPlaceholdersResponse | null>()
  const [terms, setTerms] = useState<GetCurrentSponsorPlaceholdersResponse | null>()

  const allQueriesCompleted = placeholderTerminologyQuery.isSuccess
  const anyQueryError = placeholderTerminologyQuery.isError
  const isLoading = placeholderTerminologyQuery.isLoading

  useEffect(() => {
    if (placeholderTerminologyQuery.data) {
      setOriginalTerms(placeholderTerminologyQuery.data)
      setTerms(placeholderTerminologyQuery.data)
    }
  }, [placeholderTerminologyQuery.data])

  const welcomeMessage = terms?.memberIntroduction || ''

  const hasChanges = useMemo(() => {
    return (originalTerms?.memberIntroduction || '') !== (terms?.memberIntroduction || '')
  }, [originalTerms?.memberIntroduction, terms?.memberIntroduction])

  const isValid = useMemo(() => {
    return welcomeMessage.trim().length > 0
  }, [welcomeMessage])

  const handleCancel = useCallback(() => {
    setTerms(originalTerms)
  }, [originalTerms])

  // Register unsaved changes checker with parent
  useEffect(() => {
    registerUnsavedChangesChecker('draftWelcomeMessage', () => hasChanges, handleCancel)
  }, [registerUnsavedChangesChecker, hasChanges, handleCancel])

  const handleSave = async () => {
    if (!isValid || !terms) {
      return
    }

    setIsPending(true)
    try {
      await api.updateTerminology(terms)
      setOriginalTerms(terms)

      // Mark welcome message step as complete after user saves an edit
      markStepComplete('draftWelcomeMessage')
    } catch (error) {
      console.error('Error saving welcome message:', error)
    } finally {
      setIsPending(false)
    }
  }

  const handleWelcomeMessageChange = (value: string) => {
    setTerms(prev => {
      if (prev == null) return null

      return {
        ...prev,
        memberIntroduction: value,
      }
    })
  }

  if (isLoading || anyQueryError || !allQueriesCompleted) {
    return (
      <Stack>
        <Paper radius="md" p="lg" shadow="sm" px="xl" bg="var(--mantine-color-gray-0)">
          <Group justify="space-between" align="center" p="md">
            <Title order={4}>Draft Welcome Message</Title>
            {isLoading && <Text>Loading...</Text>}
            {anyQueryError && <Text>Error loading data</Text>}
            {!allQueriesCompleted && <Text>Waiting for data to load...</Text>}
          </Group>
        </Paper>
      </Stack>
    )
  }

  return (
    <Stack>
      <Paper radius="md" p={isMobile ? 'md' : 'lg'} shadow="sm" px={isMobile ? 'md' : 'xl'}>
        <Group justify="space-between" mb="md">
          <Title order={isMobile ? 6 : 4} mb={isMobile ? 'md' : undefined}>
            Draft Welcome Message
          </Title>
          <Group>
            <Button variant="outline" onClick={handleCancel} disabled={!hasChanges || isPending}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={!isValid || isPending} loading={isPending}>
              Save
            </Button>
          </Group>
        </Group>

        {showTutorialText && (
          <Text size="md" c="dimmed" mb="md">
            Customize the welcome message your end users will receive from their Personal Sidekick when they first log
            in to the Sidekick Platform mobile app.
          </Text>
        )}

        <Textarea
          value={welcomeMessage}
          onChange={e => handleWelcomeMessageChange(e.target.value)}
          placeholder="Welcome message content"
          minRows={6}
          maxRows={isMobile ? 30 : 10}
          autosize
          disabled={isPending}
          styles={{
            input: {
              fontSize: '14px',
              lineHeight: '1.5',
            },
          }}
        />
      </Paper>
    </Stack>
  )
}
